<!--
  @component
  Generates an SVG map using the `geoPath` function from [d3-geo](https://github.com/d3/d3-geo).
 -->
<script>
    import { getContext, createEventDispatcher } from "svelte";
    import { geoPath } from "d3-geo";
    import { fade } from "svelte/transition";
    import { group } from "d3-array";
    import { select } from "d3-selection";
    import { raise, Svg, Html } from "layercake";
    import { scalePow, scaleLinear } from "d3-scale";
    import throttle from "lodash.throttle";
    import Annotations from "../_components/Annotations.svelte";
    const PATH_PREFIX = process.env.PATH_PREFIX;
    const { data, width, height } = getContext("LayerCake");

    const slugify = (str = "") =>
        str.toLowerCase().replace(/ /g, "-").replace(/\./g, "");

    /** @type {Function} projection - A D3 projection function. Pass this in as an uncalled function, e.g. `projection={geoAlbersUsa}`. */
    export let projection;

    /** @type {Number} [fixedAspectRatio] - By default, the map fills to fit the $width and $height. If instead you want a fixed-aspect ratio, like for a server-side rendered map, set that here. */
    export let fixedAspectRatio = undefined;

    export let ctx;
    export let guff;
    export let colorScale;
    export let hideTooltip = true;
    export let window_width;
    $: onMouseEvents = window_width > 900; // tied to .interact-hide-map
    $: removeCircles = window_width <= 700;

    // ANNOTATION VARIABLES
    let pt_ca_1 = [-121.47413340348243, 38.13758600062936];
    let pt_ca_2 = [-123.62833339160953, 37.53829510794309];

    let pt_tva_1 = [-86.77153151377149, 37.80350516730542];
    let pt_tva_2 = [-84.85103371234565, 42.59999446444739];

    let throttleDuration = 100;
    var fadeDuration = 200;
    $: radius = onMouseEvents ? 6 : 3.5;
    let spikeHeightScale = scaleLinear().domain([800, 1500]).range([150, 400]);
    $: spikeMaxHeight = Math.max(
        Math.min(spikeHeightScale(window_width), 400),
        150
    ); // unit: pixels400;

    let spikeWidthScale = scaleLinear().domain([800, 1500]).range([3, 6]);
    $: spike_width = Math.max(Math.min(spikeWidthScale(window_width), 6), 3);

    export let spikeMaxVal; // this is the largest number of remains in one county

    var key = ctx["type"] == "tribe" ? "total" : "pct_complete";

    $: fitSizeRange = fixedAspectRatio
        ? [100, 100 / fixedAspectRatio]
        : [$width, $height];

    $: projectionFn = projection().fitSize(
        fitSizeRange,
        $data.countriesGeojson
    );
    $: geoPathFn = geoPath(projectionFn);

    let countiesDataDict;
    $: countiesDataDict = group($data.countiesData, (d) => d.fips);
    $: stateDataDict = group($data.stateData, (d) => d.state_fips);

    var getColor = function (fips) {
        if (countiesDataDict && countiesDataDict.has(fips)) {
            return colorScale(countiesDataDict.get(fips)[0][key]);
        }
        return "white";
    };

    var getColorState = function (fips) {
        if (stateDataDict && stateDataDict.has(fips)) {
            return colorScale(stateDataDict.get(fips)[0][key]);
        }
        return "var(--gray-10)";
    };

    function getValue(id, key) {
        let value = countiesDataDict.has(id)
            ? countiesDataDict.get(id)[0][key]
            : -1;
        return value;
    }

    function spike(length) {
        return `M ${-spike_width / 2},0L0,${-length}L${spike_width / 2},0`;
    }

    $: length = scalePow([0, spikeMaxVal], [0, spikeMaxHeight]).exponent(1);

    /* --------------------------------------------
     * Here's how you would do cross-component hovers
     */
    const dispatch = createEventDispatcher();

    function handleMouseover(feature) {
        return function handleMouseoverFn(e) {
            if (feature) {
                raise(this);
                // When the element gets raised, it flashes 0,0 for a second so skip that
                if (e.layerX !== 0 && e.layerY !== 0) {
                    let properties = countiesDataDict.has(feature.id)
                        ? countiesDataDict.get(feature.id)[0]
                        : -1;
                    properties.type = "County";
                    hideTooltip = false;

                    dispatch("message", { e, props: properties });
                }
            } else {
                hideTooltip = true;
            }
        };
    }

    function handleClickState(feature) {
        if (stateDataDict.has(feature.id)) {
            let stateData = stateDataDict.get(feature.id)[0];
            if (!(stateData.total == "NA")) {
                let slug = slugify(`${stateData.state}`);
                // window.open(PATH_PREFIX + "county/" + slug, "_blank");
                window.location.href = PATH_PREFIX + "state/" + slug;
            }
        }
    }

    function handleClickCounty(feature) {
        if (countiesDataDict.has(feature.id)) {
            let countyData = countiesDataDict.get(feature.id)[0];
            if (!(countyData.total == "NA")) {
                let slug = slugify(`${countyData.county} ${countyData.state}`);
                // window.open(PATH_PREFIX + "county/" + slug, "_blank");
                window.location.href = PATH_PREFIX + "county/" + slug;
            }
        }
    }

    function handleMouseoverState(feature) {
        return function handleMouseoverFn(e) {
            if (feature) {
                raise(this);
                select(`#state-stroke-${feature.id}`).classed("hover", true);
                // When the element gets raised, it flashes 0,0 for a second so skip that
                if (e.layerX !== 0 && e.layerY !== 0) {
                    let properties = stateDataDict.has(feature.id)
                        ? stateDataDict.get(feature.id)[0]
                        : -1;
                    properties.type = "State";
                    hideTooltip = false;

                    dispatch("message", { e, props: properties });
                }
            } else {
                hideTooltip = true;
            }
        };
    }

    function handleMouseoutState(feature) {
        return function handleMouseoutFn(e) {
            if (feature) {
                select(`#state-stroke-${feature.id}`).classed("hover", false);
            }
        };
    }

    function handleClickSubunit(j, ctx) {
        if (ctx["type"] != "institution") {
            // subunits are not clickable
            window.location.href =
                PATH_PREFIX + "institution/" + j.main_institution_slug;
        }
    }

    function handleMouseoverSubunit(j) {
        return function handleMouseoverFn(e) {
            // When the element gets raised, it flashes 0,0 for a second so skip that
            if (e.layerX !== 0 && e.layerY !== 0) {
                j.type = "Institution";
                hideTooltip = false;

                dispatch("message", { e, props: j });
            }
        };
    }

    // Calculate coordinates for backgrounds of AK and HI
    $: mapPadding = $width > 1000 ? 5 : 2;
    let standardMapPadding = 2;
    $: alaskaPt1X =
        projectionFn([175.68116949831705, 49.36663922375814])[0] - mapPadding;
    $: alaskaPt1Y =
        projectionFn([175.68116949831705, 49.36663922375814])[1] + mapPadding;
    $: alaskaPt2X =
        projectionFn([-129.94626345879686, 54.450992402765806])[0] +
        standardMapPadding;
    $: alaskaPt2Y =
        projectionFn([-156.37028261460839, 71.34201224759844])[1] - mapPadding;
    $: alaskaWidth = Math.abs(alaskaPt2X - alaskaPt1X);
    $: alaskaHeight = Math.abs(alaskaPt2Y - alaskaPt1Y);

    $: hawaiiPt1X =
        projectionFn([-160.27042076006046, 18.725660640753134])[0] -
        standardMapPadding;
    $: hawaiiPt1Y =
        projectionFn([-158.27042076006046, 18.895660640753134])[1] + mapPadding;
    $: hawaiiPt2X =
        projectionFn([-154.7690154059246, 22.18811324211811])[0] + mapPadding;
    $: hawaiiPt2Y =
        projectionFn([-155.2690154059246, 22.28811324211811])[1] - mapPadding;
    $: hawaiiWidth = Math.abs(hawaiiPt2X - hawaiiPt1X);
    $: hawaiiHeight = Math.abs(hawaiiPt2Y - hawaiiPt1Y);
</script>

<Svg>
    <!-- GRADIENTS FOR BASE MAP -->
    <defs>
        <linearGradient id="gradient1" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stop-color="#ffffff" stop-opacity="1" />
            <stop offset="10%" stop-color="#ffffff" stop-opacity="0.95" />
            <stop offset="50%" stop-color="#ffffff" stop-opacity="0.5" />
            <stop offset="90%" stop-color="#ffffff" stop-opacity="0.05" />
            <stop offset="100%" stop-color="#ffffff" stop-opacity="0" />
        </linearGradient>
        <linearGradient id="gradient2" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stop-color="#ffffff" stop-opacity="0" />
            <stop offset="10%" stop-color="#ffffff" stop-opacity="0.05" />
            <stop offset="50%" stop-color="#ffffff" stop-opacity="0.5" />
            <stop offset="90%" stop-color="#ffffff" stop-opacity="0.95" />
            <stop offset="100%" stop-color="#ffffff" stop-opacity="1" />
        </linearGradient>

        <filter id="drop-shadow" x="0" y="0" width="200%" height="200%">
            <feDropShadow
                dx="0.8"
                dy="0.8"
                stdDeviation="2"
                flood-color="black"
                flood-opacity="0.1"
            />
        </filter>

        <filter id="glow">
            <feGaussianBlur
                color-interpolation-filters="sRGB"
                stdDeviation="14"
            />
        </filter>

        {#each $data.stateGeojson.features || $data.stateGeojson.features as feature}
            <clipPath id={feature.id}>
                <path class="feature-path state-path" d={geoPathFn(feature)} />
            </clipPath>
        {/each}
    </defs>

    <pattern
        id="diagonalHatch"
        patternUnits="userSpaceOnUse"
        width="4"
        height="4"
    >
        <path
            d="M-1,1 l2,-2
            M0,4 l4,-4
            M3,5 l2,-2"
            style="stroke:var(--gray-10); stroke-width:{onMouseEvents
                ? 0.5
                : 0.4}"
        />
    </pattern>

    <!-- BASEMAP -->
    <g class="map-group">
        <!-- NORTH AMERICA BACKGROUND -->
        <g class="map-group countries">
            {#each $data.countriesGeojson.features || $data.countriesGeojson.features as feature}
                <path
                    class="feature-path countries-path"
                    d={geoPathFn(feature)}
                />
            {/each}
        </g>

        <rect
            class="gradient-rect"
            fill="url(#gradient1)"
            x="0"
            y="0"
            width={$width}
            height={$height / 8}
        />

        <rect
            class="gradient-rect"
            fill="url(#gradient2)"
            x="0"
            y={$height - $height / 8}
            width={$width}
            height={$height / 8}
        />
        <!-- ALASKA AND HAWAII BACKGROUNDS -->
        <rect
            fill="white"
            stroke="var(--gray-10)"
            class="bkgd-polygon"
            x={alaskaPt1X}
            y={alaskaPt2Y}
            width={alaskaWidth}
            height={alaskaHeight}
        />
        <rect
            fill="white"
            stroke="var(--gray-10)"
            class="bkgd-polygon"
            x={hawaiiPt1X}
            y={hawaiiPt2Y}
            width={hawaiiWidth}
            height={hawaiiHeight}
        />
        <g class="map-group state-fill">
            <!-- US FILL -->
            <path
                class="feature-path state-path"
                d={geoPathFn($data.nationGeojson.features[0])}
                fill="white"
                filter="url(#drop-shadow)"
            />
        </g>

        {#if onMouseEvents}
            <rect
                fill="none"
                width="100%"
                height="100%"
                style="pointer-events:all; outline: none; cursor: auto;"
                on:mouseover={throttle(() => {
                    hideTooltip = true;
                }, throttleDuration)}
                on:focus={throttle(() => {
                    hideTooltip = true;
                }, throttleDuration)}
                on:mouseleave={throttle(() => {
                    hideTooltip = true;
                }, throttleDuration)}
            />
        {:else}
            <rect
                fill="none"
                width="100%"
                height="100%"
                style="pointer-events:all; outline: none; cursor: auto;"
            />
        {/if}

        <g class="map-group state-hatch-and-glow">
            {#each $data.stateGeojson.features || $data.stateGeojson.features as feature}
                <!-- STATE GLOW-->
                {#if stateDataDict.has(feature.id)}
                    {#if onMouseEvents}
                        <path
                            class="feature-path state-glow state-glow-hoverable"
                            clip-path="url(#{feature.id})"
                            filter="url(#glow)"
                            stroke={ctx["type"] == "institution"
                                ? getColorState(feature.id)
                                : "var(--colorScale100)"}
                            stroke-width="3"
                            fill="rgba(255, 255, 255, 0)"
                            d={geoPathFn(feature)}
                            on:mouseover={throttle(
                                handleMouseoverState(feature),
                                throttleDuration
                            )}
                            on:mouseout={throttle(
                                handleMouseoutState(feature),
                                throttleDuration
                            )}
                            on:blur={throttle(
                                handleMouseoutState(feature),
                                throttleDuration
                            )}
                            on:focus={throttle(
                                handleMouseoverState(feature),
                                throttleDuration
                            )}
                            on:click={handleClickState(feature)}
                            on:keydown={handleClickState(feature)}
                        />
                    {:else}
                        <path
                            class="feature-path state-glow"
                            clip-path="url(#{feature.id})"
                            filter="url(#glow)"
                            stroke={ctx["type"] == "institution"
                                ? getColorState(feature.id)
                                : "var(--colorScale100)"}
                            stroke-width="3"
                            fill="rgba(255, 255, 255, 0)"
                            d={geoPathFn(feature)}
                        />
                    {/if}
                {:else}
                    <!-- STATE HATCH-->
                    <path
                        class="feature-path"
                        fill="url(#diagonalHatch)"
                        stroke="var(--gray-10)"
                        stroke-width="1"
                        pointer-events="none"
                        d={geoPathFn(feature)}
                    />
                {/if}
            {/each}
        </g>

        <!-- COUNTY CHOROPLETH + SPIKES -->
        <g
            class="map-group"
            in:fade={{ delay: fadeDuration }}
            out:fade={{ duration: fadeDuration }}
        >
            <!-- COUNTY CHOROPLETH -->
            {#each $data.countiesGeojson.features || $data.countiesGeojson.features as feature}
                <g class="county-path-group">
                    {#if countiesDataDict.has(feature.id) && getValue(feature.id, key) != "NA"}
                        <path
                            class="feature-path county-path"
                            fill="white"
                            d={geoPathFn(feature)}
                        />
                    {/if}

                    {#if onMouseEvents}
                        <path
                            class="feature-path county-path county-path-hoverable {ctx[
                                'type'
                            ] == 'homepage' &&
                            (!countiesDataDict.has(feature.id) ||
                                getValue(feature.id, key) == 'NA')
                                ? 'county-path-empty-hoverable'
                                : ''} "
                            id="county-path-{feature.id}"
                            fill={countiesDataDict.has(feature.id)
                                ? getValue(feature.id, key) != "NA"
                                    ? ctx["type"] == "tribe"
                                        ? "var(--colorScale100)"
                                        : getColor(feature.id)
                                    : "none"
                                : "none"}
                            fill-opacity={countiesDataDict &&
                            countiesDataDict.has(feature.id)
                                ? 0.2
                                : 1}
                            stroke={countiesDataDict.has(feature.id)
                                ? ctx["type"] == "tribe"
                                    ? "var(--colorScale100)"
                                    : getColor(feature.id)
                                : "none"}
                            d={geoPathFn(feature)}
                            on:mouseover={throttle(
                                handleMouseover(feature),
                                throttleDuration
                            )}
                            on:focus={throttle(
                                handleMouseover(feature),
                                throttleDuration
                            )}
                            on:click={handleClickCounty(feature)}
                            on:keydown={handleClickCounty(feature)}
                        />
                    {:else}
                        <path
                            class="feature-path county-path {ctx['type'] ==
                                'homepage' &&
                            (!countiesDataDict.has(feature.id) ||
                                getValue(feature.id, key) == 'NA')
                                ? 'county-path-empty'
                                : ''} "
                            id="county-path-{feature.id}"
                            fill={countiesDataDict.has(feature.id)
                                ? getValue(feature.id, key) != "NA"
                                    ? ctx["type"] == "tribe"
                                        ? "var(--colorScale100)"
                                        : getColor(feature.id)
                                    : "none"
                                : "none"}
                            fill-opacity={countiesDataDict &&
                            countiesDataDict.has(feature.id)
                                ? 0.2
                                : 1}
                            stroke={countiesDataDict.has(feature.id)
                                ? ctx["type"] == "tribe"
                                    ? "var(--colorScale100)"
                                    : getColor(feature.id)
                                : "none"}
                            d={geoPathFn(feature)}
                        />
                    {/if}
                </g>
            {/each}

            {#each $data.stateGeojson.features || $data.stateGeojson.features as feature}
                <!-- STATE STROKE-->
                {#if stateDataDict.has(feature.id)}
                    <path
                        class="feature-path state-stroke"
                        id="state-stroke-{feature.id}"
                        stroke={ctx["type"] == "institution"
                            ? getColorState(feature.id)
                            : "var(--colorScale100)"}
                        fill="none"
                        pointer-events="none"
                        d={geoPathFn(feature)}
                    />
                {/if}
            {/each}

            <!-- RIVERS -->
            {#if ctx["type"] == "homepage"}
                <g class="map-group rivers">
                    {#each $data.riversGeojson.features || $data.riversGeojson.features as feature}
                        <path
                            class="feature-path rivers-path"
                            stroke="var(--warm-70)"
                            stroke-width="1.5"
                            stroke-opacity="0.4"
                            fill="none"
                            d={geoPathFn(feature)}
                        />
                    {/each}
                </g>
            {/if}

            <g class="county-path-group">
                {#each $data.countiesGeojson.features || $data.countiesGeojson.features as feature}
                    <!-- SPIKES BLUE -->
                    {#if countiesDataDict.has(feature.id) && countiesDataDict.get(feature.id)[0][key] != "NA" && (ctx["type"] == "tribe" || countiesDataDict.get(feature.id)[0][key] >= 0.5)}
                        <path
                            class="spike"
                            id="spike-{feature.id}"
                            transform={`translate(${geoPathFn.centroid(
                                feature
                            )})`}
                            d={spike(
                                length(
                                    countiesDataDict.get(feature.id)[0]["total"]
                                )
                            )}
                            fill={ctx["type"] == "tribe"
                                ? "var(--colorScale100)"
                                : colorScale(
                                      countiesDataDict.get(feature.id)[0][key]
                                  )}
                            stroke={ctx["type"] == "tribe"
                                ? "var(--colorScale100)"
                                : colorScale(
                                      countiesDataDict.get(feature.id)[0][key]
                                  )}
                        />
                    {/if}
                    <!-- SPIKES RED -->
                    {#if countiesDataDict.has(feature.id) && countiesDataDict.get(feature.id)[0][key] != "NA" && (ctx["type"] == "tribe" || countiesDataDict.get(feature.id)[0][key] < 0.5)}
                        <path
                            class="spike"
                            id="spike-{feature.id}"
                            transform={`translate(${geoPathFn.centroid(
                                feature
                            )})`}
                            d={spike(
                                length(
                                    countiesDataDict.get(feature.id)[0]["total"]
                                )
                            )}
                            fill={ctx["type"] == "tribe"
                                ? "var(--colorScale100)"
                                : colorScale(
                                      countiesDataDict.get(feature.id)[0][key]
                                  )}
                            stroke={ctx["type"] == "tribe"
                                ? "var(--colorScale100)"
                                : colorScale(
                                      countiesDataDict.get(feature.id)[0][key]
                                  )}
                        />
                    {/if}
                {/each}
            </g>
        </g>

        <!-- STATE LABELS -->
        {#each $data.stateGeojson.features || $data.stateGeojson.features as feature}
            {#if geoPathFn.centroid(feature)[0] && feature.properties.hide_abb != true && window_width > 400}
                <text
                    class="state-label state-label-bkgd"
                    x={feature.properties.label_pt &&
                    (ctx["type"] == "homepage" ||
                        feature.properties.override_centroid)
                        ? projectionFn(feature.properties.label_pt)[0]
                        : geoPathFn.centroid(feature)[0]}
                    y={feature.properties.label_pt &&
                    (ctx["type"] == "homepage" ||
                        feature.properties.override_centroid)
                        ? projectionFn(feature.properties.label_pt)[1]
                        : geoPathFn.centroid(feature)[1]}
                >
                    {feature.properties.abb}
                </text>
                <text
                    class="state-label"
                    x={feature.properties.label_pt &&
                    (ctx["type"] == "homepage" ||
                        feature.properties.override_centroid)
                        ? projectionFn(feature.properties.label_pt)[0]
                        : geoPathFn.centroid(feature)[0]}
                    y={feature.properties.label_pt &&
                    (ctx["type"] == "homepage" ||
                        feature.properties.override_centroid)
                        ? projectionFn(feature.properties.label_pt)[1]
                        : geoPathFn.centroid(feature)[1]}
                >
                    {feature.properties.abb}
                </text>
            {/if}
        {/each}
    </g>

    <!-- INSTITUTION SYMBOLS -->
    {#if ctx["type"] != "homepage"}
        <g
            class="map-group institution-symbols"
            in:fade={{ delay: fadeDuration }}
            out:fade={{ duration: fadeDuration }}
        >
            {#each $data.subunits || $data.subunits as subunit}
                <!-- show institution circles -->
                {#if projectionFn( [+subunit.institution_lon, +subunit.institution_lat] )}
                    <g class="institution-shape">
                        <!-- INSTITUTION REMAINS CIRCLE -->
                        {#if ctx["type"] == "tribe" && !removeCircles}
                            <circle
                                class="circle-institution"
                                id="circle-institution-{subunit.institution_slug}"
                                cx={projectionFn([
                                    +subunit.institution_lon,
                                    +subunit.institution_lat,
                                ])[0]}
                                cy={projectionFn([
                                    +subunit.institution_lon,
                                    +subunit.institution_lat,
                                ])[1]}
                                r={Math.sqrt(
                                    (ctx["type"] == "tribe"
                                        ? +subunit.total
                                        : +subunit.pending) /
                                        Math.PI /
                                        2
                                )}
                            />
                        {/if}

                        <!-- INSTITUTION CENTER -->
                        {#if onMouseEvents}
                            <rect
                                class="rect-institution rect-institution-hoverable"
                                id="rect-institution-{subunit.institution_slug}"
                                x={projectionFn([
                                    +subunit.institution_lon,
                                    +subunit.institution_lat,
                                ])[0] -
                                    radius / 2}
                                y={projectionFn([
                                    +subunit.institution_lon,
                                    +subunit.institution_lat,
                                ])[1] -
                                    radius / 2}
                                width={radius}
                                height={radius}
                                on:mouseover={throttle(
                                    handleMouseoverSubunit(subunit),
                                    throttleDuration
                                )}
                                on:focus={throttle(
                                    handleMouseoverSubunit(subunit),
                                    throttleDuration
                                )}
                                on:click={handleClickSubunit(subunit, ctx)}
                                on:keydown={handleClickSubunit(subunit, ctx)}
                            />
                        {:else}
                            <rect
                                class="rect-institution"
                                id="rect-institution-{subunit.institution_slug}"
                                x={projectionFn([
                                    +subunit.institution_lon,
                                    +subunit.institution_lat,
                                ])[0] -
                                    radius / 2}
                                y={projectionFn([
                                    +subunit.institution_lon,
                                    +subunit.institution_lat,
                                ])[1] -
                                    radius / 2}
                                width={radius}
                                height={radius}
                            />
                        {/if}
                    </g>
                {/if}
            {/each}
        </g>
    {/if}
</Svg>

{#if ctx["type"] == "homepage"}
    <Html pointerEvents={false}>
        <Annotations {projectionFn} {guff} />
    </Html>
{/if}

<style lang="scss">
    .countries-path {
        fill: var(--warm-10);
        fill-opacity: 0.7;
        pointer-events: none;
    }

    .county-path {
        stroke-width: 0.05px;
        stroke-linejoin: round;
        transition: stroke-width var(--transition);
    }

    .state-path {
        stroke: var(--gray-10);
        stroke-width: 1px;
        pointer-events: none;
        stroke-linejoin: round;
    }

    .state-glow {
        stroke-opacity: 0.5;
    }

    .state-glow-hoverable {
        cursor: pointer;
    }

    .state-stroke {
        stroke-width: 1.5px;
        transition: stroke-opacity var(--transition);
        stroke-opacity: 0.05;
        stroke-linejoin: round;
    }

    .county-path-empty-hoverable {
        pointer-events: visible;
    }

    .map-group {
        &:focus {
            outline: none;
        }
        cursor: pointer;
    }

    .county-path-group {
        .county-path-hoverable {
            cursor: pointer;
        }

        &:hover .county-path-hoverable {
            fill-opacity: 0.6;
            stroke-width: 1px;
        }
    }
    .spike {
        fill-opacity: 0.6;
        stroke-opacity: 0.9;
        transition: fill-opacity var(--transition),
            stroke-opacity var(--transition);
        pointer-events: none;
    }

    .county-path-empty {
        fill: rgba(255, 255, 255, 0);
        stroke: black;
        stroke-opacity: 0;
    }

    .county-path-empty-hoverable {
        &:hover {
            stroke-opacity: 1;
        }
    }

    .circle-institution {
        fill: var(--warm-50);
        fill-opacity: 0.1;
        stroke: var(--warm-50);
        stroke-opacity: 0.3;
        stroke-width: 0.25px;
        transition: stroke-opacity var(--transition),
            stroke-width var(--transition), fill-opacity var(--transition);
        pointer-events: none;
    }

    .rect-institution {
        fill: #000;
        stroke: white;
        transition: stroke-width var(--transition),
            stroke-width var(--transition);
        stroke-width: 0.75px;
        transform-box: fill-box;
        transform-origin: center;
        transform: rotate(45deg);
        fill-opacity: 0.7;
    }

    .rect-institution-hoverable {
        cursor: pointer;
    }

    .institution-shape {
        &:hover {
            .circle-institution {
                stroke-opacity: 1 !important;
                stroke-width: 1px !important;
                stroke: black !important;
            }
            .rect-institution-hoverable {
                stroke: #000;
                stroke-width: 6px;
            }
        }
    }

    .state-label {
        font-size: var(--scale-3);
        fill: var(--gray-60);
        font-weight: 300;
        text-transform: uppercase;
        letter-spacing: 1px;
        text-anchor: middle;
    }

    .state-label-bkgd {
        fill: white;
        stroke: white;
        stroke-width: 2px;
        filter: drop-shadow(0px 0px 3px rgba(255, 255, 255, 0.9));
    }

    /**
   * Disable the outline on feature click.
   * Depending on map funtionality and accessiblity issues,
   * you may not want this rule. Read more:
   * https://developer.mozilla.org/en-US/docs/Web/CSS/:focus
   * https://github.com/mhkeller/layercake/issues/63
   */
    .feature-path:focus {
        outline: none;
    }
</style>
